import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trophy, Medal, Users, Check, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { likeHeaders } from "@/lib/useLikes";
import { storage } from "@/lib/storage";
import { trackEvent } from "@/lib/analytics";
import { useTrophies } from "@/lib/useTrophies";

const API = process.env.REACT_APP_BACKEND_URL;
const NICK_KEY = "atlas.nickname";

const fetchBoard = async () => {
  const r = await fetch(`${API}/api/daily/leaderboard`, { headers: likeHeaders() });
  if (!r.ok) throw new Error("leaderboard");
  return r.json();
};

/** Posts today's score under a nickname (one per device/day). Resolves to the server response. */
export async function submitScore({ day, picks, nickname }) {
  const r = await fetch(`${API}/api/daily/score`, {
    method: "POST", headers: { "Content-Type": "application/json", ...likeHeaders() },
    body: JSON.stringify({ day, score: picks.filter(Boolean).length, picks, nickname }),
  });
  if (!r.ok) throw new Error(r.status === 400 ? "nickname" : "server");
  storage.set(NICK_KEY, nickname);
  return r.json();
}

export function NicknameForm({ day, picks, onDone }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [nick, setNick] = useState(storage.get(NICK_KEY) || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await submitScore({ day, picks, nickname: nick.trim() });
      trackEvent("daily_score_submitted", { score: picks.filter(Boolean).length });
      qc.invalidateQueries({ queryKey: ["daily-leaderboard"] });
      onDone(nick.trim());
    } catch (err) { setError(err.message === "nickname" ? t("nick_invalid") : t("review_error")); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-stoneLine bg-sand/60 p-3 flex flex-col gap-2" data-testid="nickname-form">
      <label className="text-xs font-semibold text-ink flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-ochre" /> {t("nick_title")}</label>
      <div className="flex gap-2">
        <input value={nick} onChange={(e) => setNick(e.target.value)} maxLength={20} minLength={2} required placeholder={t("nick_placeholder")} data-testid="nickname-input"
          className="flex-1 min-w-0 rounded-lg border border-stoneLine bg-card px-3 py-2 text-sm focus:outline-none focus:border-ochre" />
        <button type="submit" disabled={busy || nick.trim().length < 2} data-testid="nickname-submit" className="px-4 py-2 rounded-full bg-ink text-sand text-sm font-medium hover:bg-ochre disabled:opacity-50 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("nick_save")}
        </button>
      </div>
      <span className="text-[10px] text-muted-foreground">{t("nick_hint")}</span>
      {error && <span className="text-xs text-red-600" data-testid="nickname-error">{error}</span>}
    </form>
  );
}

function Row({ r }) {
  const medal = r.rank <= 3 ? ["text-amber-500", "text-slate-400", "text-amber-700"][r.rank - 1] : null;
  return (
    <li className={`flex items-center gap-3 px-3 py-2 rounded-xl ${r.me ? "bg-ochre/10 border border-ochre/40" : ""}`} data-testid={`lb-row-${r.rank}`}>
      <span className="w-7 text-center font-bold text-sm text-ink">{medal ? <Medal className={`w-4 h-4 inline ${medal}`} /> : r.rank}</span>
      <span className="flex-1 min-w-0 truncate text-sm font-medium text-ink">{r.nickname}{r.me && <Check className="w-3.5 h-3.5 inline ms-1 text-ochre" />}</span>
      <span className="text-xs text-muted-foreground">{r.days}d{r.perfect ? ` · ${r.perfect}★` : ""}</span>
      <span className="stat-number text-lg text-ink w-10 text-end">{r.total}</span>
    </li>
  );
}

/** Weekly (Mon→Sun) leaderboard of the Daily Challenge; highlights the current device. */
export default function Leaderboard() {
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["daily-leaderboard"], queryFn: fetchBoard, staleTime: 30_000 });
  const { data: trophies } = useTrophies();
  if (!data || data.players === 0) return null;
  const champ = trophies?.latest;
  const inTop = data.top.some((r) => r.me);
  const meRow = data.me && !inTop ? { ...data.me, me: true } : null;
  return (
    <section className="w-full rounded-3xl border border-stoneLine bg-card p-5 sm:p-6" data-testid="leaderboard">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-base md:text-lg font-display font-bold text-ink flex items-center gap-2"><Trophy className="w-5 h-5 text-ochre" /> {t("lb_title")}</h2>
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="lb-players"><Users className="w-3.5 h-3.5" /> {t("lb_players").replace("{n}", data.players)}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{t("lb_sub")}</p>
      {champ && <p className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center gap-1.5" data-testid="lb-champion"><Trophy className="w-3.5 h-3.5" /> {t("lb_last_champion").replace("{nick}", champ.nickname).replace("{total}", champ.total)}</p>}
      <ol className="space-y-1">
        {data.top.map((r) => <Row key={r.rank} r={r} />)}
        {meRow && <><li className="text-center text-muted-foreground text-xs">···</li><Row r={meRow} /></>}
      </ol>
    </section>
  );
}
